/**
 * The display-mode change cross-fades the colour tokens (T1 of the COP redesign).
 * `html.theme-fade` in globals.css carries the transition; this is the only code that
 * puts it on and takes it off, so the class never outlives the change that asked for it.
 *
 * Under `prefers-reduced-motion` the class is never added at all. The CSS also kills the
 * transition under that query, so this is belt and braces - but it keeps a stray class
 * off `<html>` for an operator who asked for no motion.
 */

/** `--duration-base` (200 ms) plus a frame, so the class outlives the transition. */
export const THEME_FADE_MS = 260;

/**
 * The pending removal, if one is in flight. A second change inside `THEME_FADE_MS` must
 * cancel the first timer: otherwise it fires mid-way through the second transition, strips
 * the class, and the colours snap to their end values - the exact jump the fade exists to
 * avoid.
 */
let pendingRemoval: number | undefined;

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  } catch {
    return false;
  }
}

/**
 * Set the theme and cross-fade the tokens for that one change.
 *
 * @param setTheme next-themes' setter.
 * @param next The mode being switched to.
 */
export function setThemeWithFade(setTheme: (theme: string) => void, next: 'light' | 'dark'): void {
  if (!prefersReducedMotion()) {
    const root = document.documentElement;
    if (pendingRemoval !== undefined) window.clearTimeout(pendingRemoval);
    root.classList.add('theme-fade');
    pendingRemoval = window.setTimeout(() => {
      root.classList.remove('theme-fade');
      pendingRemoval = undefined;
    }, THEME_FADE_MS);
  }
  setTheme(next);
}
